import { useState, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ImageIcon,
  Upload,
  Link,
  Plus,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { cn } from '../../../lib/utils';
import { uploadMedia } from '../../../services/blog.service';
import type { BlogCategory, BlogTag } from '../../../types';

interface PostSettingsSidebarProps {
  postTitle: string;
  slug: string;
  onSlugChange: (v: string) => void;
  excerpt: string;
  onExcerptChange: (v: string) => void;
  coverImageUrl: string;
  onCoverImageChange: (v: string) => void;
  seoTitle: string;
  onSeoTitleChange: (v: string) => void;
  seoDescription: string;
  onSeoDescriptionChange: (v: string) => void;
  categories: BlogCategory[];
  selectedCategoryIds: string[];
  onCategoryChange: (ids: string[]) => void;
  onCreateCategory?: (data: { name: string; color?: string }) => Promise<{ success: boolean; error?: string }>;
  tags: BlogTag[];
  selectedTagIds: string[];
  onTagChange: (ids: string[]) => void;
  onCreateTag?: (name: string) => Promise<{ success: boolean; error?: string }>;
  accessToken?: string | null;
}

// ---------------------------------------------------------------------------
// Collapsible section helper
// ---------------------------------------------------------------------------

interface SectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function Section({ title, defaultOpen = true, children }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold hover:bg-muted/50 transition-colors"
      >
        <span>{title}</span>
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {children}
        </div>
      )}
      <div className="border-b" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PostSettingsSidebar
// ---------------------------------------------------------------------------

export function PostSettingsSidebar({
  postTitle,
  slug,
  onSlugChange,
  excerpt,
  onExcerptChange,
  coverImageUrl,
  onCoverImageChange,
  seoTitle,
  onSeoTitleChange,
  seoDescription,
  onSeoDescriptionChange,
  categories,
  selectedCategoryIds,
  onCategoryChange,
  onCreateCategory,
  tags,
  selectedTagIds,
  onTagChange,
  onCreateTag,
  accessToken,
}: PostSettingsSidebarProps) {
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverUploadError, setCoverUploadError] = useState<string | null>(null);
  // Track whether user has manually edited the SEO title
  const [seoTitleTouched, setSeoTitleTouched] = useState(!!seoTitle);
  // Inline creation states
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);

  const toggleCategory = useCallback(
    (id: string, checked: boolean) => {
      if (checked) {
        onCategoryChange([...selectedCategoryIds, id]);
      } else {
        onCategoryChange(selectedCategoryIds.filter((c) => c !== id));
      }
    },
    [selectedCategoryIds, onCategoryChange]
  );

  const toggleTag = useCallback(
    (id: string, checked: boolean) => {
      if (checked) {
        onTagChange([...selectedTagIds, id]);
      } else {
        onTagChange(selectedTagIds.filter((t) => t !== id));
      }
    },
    [selectedTagIds, onTagChange]
  );

  const handleCreateCategory = useCallback(async () => {
    const name = newCategoryName.trim();
    if (!name || !onCreateCategory) return;
    setCreatingCategory(true);
    const result = await onCreateCategory({ name });
    setCreatingCategory(false);
    if (result.success) setNewCategoryName('');
  }, [newCategoryName, onCreateCategory]);

  const handleCreateTag = useCallback(async () => {
    const name = newTagName.trim();
    if (!name || !onCreateTag) return;
    setCreatingTag(true);
    const result = await onCreateTag(name);
    setCreatingTag(false);
    if (result.success) setNewTagName('');
  }, [newTagName, onCreateTag]);

  return (
    <div className="divide-y divide-border">
      {/* Permalink / Slug */}
      <Section title="Permalink" defaultOpen>
        <div className="space-y-1.5">
          <Label htmlFor="post-slug" className="text-xs text-muted-foreground flex items-center gap-1">
            <Link className="h-3 w-3" />
            Slug da URL
          </Label>
          <Input
            id="post-slug"
            value={slug}
            onChange={(e) => onSlugChange(e.target.value)}
            placeholder="meu-post-sobre-acrilico"
            className="h-8 text-xs font-mono"
          />
          {slug && (
            <p className="text-xs text-muted-foreground break-all">
              /blog/<span className="text-foreground">{slug}</span>
            </p>
          )}
        </div>
      </Section>

      {/* Excerpt */}
      <Section title="Resumo" defaultOpen>
        <div className="space-y-1.5">
          <Label htmlFor="post-excerpt" className="text-xs text-muted-foreground">
            Resumo do post
          </Label>
          <Textarea
            id="post-excerpt"
            value={excerpt}
            onChange={(e) => onExcerptChange(e.target.value)}
            placeholder="Uma breve descrição exibida nas listagens..."
            className="text-xs min-h-[80px] resize-none"
            maxLength={300}
          />
          <p className="text-xs text-muted-foreground text-right">
            {excerpt.length}/300
          </p>
        </div>
      </Section>

      {/* Cover Image */}
      <Section title="Imagem de capa" defaultOpen>
        <div className="space-y-2">
          {coverImageUrl ? (
            <div className="relative rounded-md overflow-hidden border aspect-video bg-muted">
              <img
                src={coverImageUrl}
                alt="Capa do post"
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center rounded-md border border-dashed aspect-video bg-muted/30">
              <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
            </div>
          )}
          <label className="block">
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              disabled={coverUploading}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setCoverUploading(true);
                setCoverUploadError(null);
                try {
                  const media = await uploadMedia(file);
                  onCoverImageChange(media.url);
                } catch {
                  setCoverUploadError('Falha ao enviar imagem.');
                } finally {
                  setCoverUploading(false);
                  e.target.value = '';
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              className="w-full h-8 text-xs"
              asChild
              disabled={coverUploading}
            >
              <span>
                <Upload className="h-3 w-3 mr-1.5" />
                {coverUploading ? 'Enviando...' : coverImageUrl ? 'Trocar imagem' : 'Enviar imagem de capa'}
              </span>
            </Button>
          </label>
          {coverUploadError && <p className="text-xs text-destructive">{coverUploadError}</p>}
          <Label htmlFor="cover-url" className="text-xs text-muted-foreground">
            ou colar URL
          </Label>
          <Input
            id="cover-url"
            value={coverImageUrl}
            onChange={(e) => onCoverImageChange(e.target.value)}
            placeholder="https://..."
            className="h-8 text-xs"
          />
        </div>
      </Section>

      {/* Categories */}
      <Section title="Categorias" defaultOpen={categories.length > 0}>
        {categories.length > 0 && (
          <div className="space-y-2">
            {categories.map((cat) => (
              <label
                key={cat.id}
                className="flex items-center gap-2 cursor-pointer group"
              >
                <Checkbox
                  id={`cat-${cat.id}`}
                  checked={selectedCategoryIds.includes(cat.id)}
                  onCheckedChange={(checked) => toggleCategory(cat.id, !!checked)}
                />
                <span
                  className={cn(
                    'text-sm transition-colors',
                    selectedCategoryIds.includes(cat.id)
                      ? 'text-foreground'
                      : 'text-muted-foreground group-hover:text-foreground'
                  )}
                >
                  {cat.name}
                </span>
                {cat.color && (
                  <span
                    className="ml-auto w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: cat.color }}
                  />
                )}
              </label>
            ))}
          </div>
        )}
        {categories.length === 0 && (
          <p className="text-xs text-muted-foreground">Nenhuma categoria cadastrada.</p>
        )}
        {onCreateCategory && (
          <div className="flex items-center gap-1.5 mt-2">
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Nova categoria..."
              className="h-7 text-xs flex-1"
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateCategory(); }}
              disabled={creatingCategory}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim() || creatingCategory}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </Section>

      {/* Tags */}
      <Section title="Tags" defaultOpen={false}>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => {
              const isSelected = selectedTagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id, !isSelected)}
                  className={cn(
                    'px-2 py-0.5 rounded-full text-xs border transition-colors',
                    isSelected
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-transparent text-muted-foreground border-border hover:border-primary hover:text-foreground'
                  )}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}
        {tags.length === 0 && (
          <p className="text-xs text-muted-foreground">Nenhuma tag cadastrada.</p>
        )}
        {onCreateTag && (
          <div className="flex items-center gap-1.5 mt-2">
            <Input
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder="Nova tag..."
              className="h-7 text-xs flex-1"
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); }}
              disabled={creatingTag}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={handleCreateTag}
              disabled={!newTagName.trim() || creatingTag}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </Section>

      {/* SEO */}
      <Section title="SEO" defaultOpen={false}>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="seo-title" className="text-xs text-muted-foreground">
              Título SEO
            </Label>
            <Input
              id="seo-title"
              value={seoTitle}
              onChange={(e) => {
                setSeoTitleTouched(true);
                onSeoTitleChange(e.target.value);
              }}
              placeholder={postTitle || 'Título para mecanismos de busca...'}
              className="h-8 text-xs"
              maxLength={70}
            />
            {!seoTitleTouched && postTitle && !seoTitle && (
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => onSeoTitleChange(postTitle.slice(0, 70))}
              >
                Usar título do post
              </button>
            )}
            <p
              className={cn(
                'text-xs text-right',
                (seoTitle || postTitle).length > 60 ? 'text-amber-500' : 'text-muted-foreground'
              )}
            >
              {seoTitle.length}/70
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="seo-desc" className="text-xs text-muted-foreground">
              Descrição SEO
            </Label>
            <Textarea
              id="seo-desc"
              value={seoDescription}
              onChange={(e) => onSeoDescriptionChange(e.target.value)}
              placeholder="Descrição exibida nos resultados de busca..."
              className="text-xs min-h-[80px] resize-none"
              maxLength={160}
            />
            <p
              className={cn(
                'text-xs text-right',
                seoDescription.length > 150 ? 'text-amber-500' : 'text-muted-foreground'
              )}
            >
              {seoDescription.length}/160
            </p>
          </div>

          {/* Google SERP Preview */}
          {(seoTitle || seoDescription || postTitle) && (
            <div className="rounded-md border p-3 space-y-1 bg-muted/20">
              <p className="text-xs text-muted-foreground mb-1 font-medium">Pré-visualização Google</p>
              <p className="text-sm text-blue-400 font-medium leading-tight line-clamp-1">
                {seoTitle || postTitle || 'Título do post'}
              </p>
              <p className="text-xs text-green-600/80">
                mundotransparente.com.br/blog/{'\u00A0'}
              </p>
              <p className="text-xs text-muted-foreground leading-tight line-clamp-2">
                {seoDescription || 'Descrição do post...'}
              </p>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
